import { useParams } from 'next/navigation';
import { useDispatch } from 'react-redux';
import useSWR, { KeyedMutator } from 'swr';

import { PaymentInfo } from '@/components/pages/Plan/PlanDetails/types';
import { setBannerMessage } from '@/store/actions';
import { CalmError, GenericCalmError, isCalmError } from '@/utils/apiRequest/errors';
import { calmLogger } from '@/utils/calmLogger';
import { isSelfServePlan } from '@/utils/SkuUtils';

import { useApi } from './useApi';
import { useOptionalPartner } from './usePartner';

export const usePaymentInfo = (): {
	data: PaymentInfo | undefined;
	mutate: KeyedMutator<PaymentInfo | undefined>;
	error: CalmError | undefined;
	isLoading: boolean;
} => {
	const params = useParams<{ partnerId: string | undefined }>();
	const partnerId = params?.partnerId || '';
	const optionalPartner = useOptionalPartner(partnerId);
	const dispatch = useDispatch();
	const apiRequest = useApi();

	const { data, mutate, error, isLoading } = useSWR(
		// Return null as the key when partnerId is empty/undefined to prevent SWR from attempting to call the query function
		partnerId && partnerId.trim() ? `b2b/partners/${partnerId}/payment-instrument` : null,
		async (endpoint): Promise<PaymentInfo | undefined> => {
			try {
				if (
					!partnerId ||
					!optionalPartner?.data ||
					!isSelfServePlan(optionalPartner?.data.vouched_plan_sku)
				) {
					return;
				}
				const { data } = await apiRequest({
					endpoint,
					method: 'GET',
				});
				return data;
			} catch (err) {
				calmLogger.error('Teams : Error fetching payment instrument', { partnerId }, err);
				dispatch(
					setBannerMessage({
						message: 'Failed to fetch payment info',
						isError: true,
						flash: true,
					}),
				);
				throw err;
			}
		},
	);

	return {
		data,
		mutate,
		error: error ? (isCalmError(error) ? error : GenericCalmError) : undefined,
		isLoading,
	};
};
